import Header from "./Header";
import Footer from "./Footer";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function Contact() {
  const [sectionRef, sectionInView] = useScrollReveal(0.1);

  return (
    <>
      <Header />

      <section className="my-7" ref={sectionRef}>
        <div className="container">
          <div className="row g-3">
            {/* ── Left: Info & Map ──────────────────────────────────────── */}
            <motion.div
              className="col-lg-6 col-12"
              variants={slideLeft}
              initial="hidden"
              animate={sectionInView ? "visible" : "hidden"}
            >
              <motion.h2
                className="h1 fw-semibold ls-sm text-success lh-1 m-0"
                variants={fadeUp}
                initial="hidden"
                animate={sectionInView ? "visible" : "hidden"}
                custom={0}
              >
                Get in Touch With Us
              </motion.h2>
              <motion.p
                className="fs-4 lh-1"
                variants={fadeUp}
                initial="hidden"
                animate={sectionInView ? "visible" : "hidden"}
                custom={1}
              >
                If you have business inquiries or other questions, please fill
                out the following form to contact us.
              </motion.p>
              <div className="mt-n3 pt-1">
                <img src="/assets/images/green_shap.webp" width="150" />
              </div>

              <motion.div
                className="mt-4 fs-4 lh-sm"
                variants={staggerContainer}
                initial="hidden"
                animate={sectionInView ? "visible" : "hidden"}
              >
                {[
                  {
                    icon: "bi-telephone",
                    content: <span>+91 6386191243</span>,
                  },
                  {
                    icon: "bi-envelope-open",
                    content: <span>support@swinglearnllp.com</span>,
                  },
                  {
                    icon: "bi-geo-alt",
                    content: (
                      <span>
                        SLN Academy
                        <br />
                        Operated by: Swinglearn Network LLP <br />
                        52, Dindayal Ka Pura, Ahirara, Bharwari, <br />
                        Near Kaushambi Police Station, <br />
                        Manjhanpur, Kaushambi, Uttar Pradesh – 212201, India
                      </span>
                    ),
                  },
                ].map(({ icon, content }, i) => (
                  <motion.p
                    key={i}
                    className="d-flex align-items-start"
                    variants={fadeUp}
                    custom={i}
                    whileHover={{ x: 4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <i className={`bi ${icon} text-primary me-2 mt-1`}></i>
                    {content}
                  </motion.p>
                ))}
              </motion.div>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate={sectionInView ? "visible" : "hidden"}
                custom={4}
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 150 }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d230275.1563770137!2d81.2959981456843!3d25.60327738104286!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399b2772144222c9%3A0x75375cf3253a7c16!2sSwinglearn%20Network%20llp!5e0!3m2!1sen!2sin!4v1767433514524!5m2!1sen!2sin"
                  height="350"
                  className="w-100 rounded-4 mt-4"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </motion.div>
            </motion.div>

            {/* ── Right: Form ───────────────────────────────────────────── */}
            <motion.div
              className="col-lg-6 col-12"
              variants={slideRight}
              initial="hidden"
              animate={sectionInView ? "visible" : "hidden"}
            >
              <motion.form
                className="row needs-validation gx-2 gy-3 rounded-4 bg-light p-6 pt-5"
                noValidate
                variants={staggerContainer}
                initial="hidden"
                animate={sectionInView ? "visible" : "hidden"}
              >
                <motion.div
                  className="col-12 col-md-6"
                  variants={fadeUp}
                  custom={0}
                >
                  <label className="form-label" htmlFor="fname">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="last name"
                    id="fname"
                    placeholder="Enter Full Name"
                    required
                  />
                </motion.div>

                <motion.div
                  className="col-12 col-md-6"
                  variants={fadeUp}
                  custom={1}
                >
                  <label className="form-label" htmlFor="lname">
                    Email ID <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="last name"
                    id="lname"
                    placeholder="Enter Email ID"
                    required
                  />
                </motion.div>

                <motion.div
                  className="col-12 col-md-6"
                  variants={fadeUp}
                  custom={2}
                >
                  <label className="form-label" htmlFor="phone">
                    Phone No. <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder="Phone"
                    required
                  />
                </motion.div>

                <motion.div
                  className="col-12 col-md-6"
                  variants={fadeUp}
                  custom={3}
                >
                  <label
                    className="text-dark form-label"
                    htmlFor="contactReason"
                  >
                    Reason <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    required
                  />
                </motion.div>

                <motion.div className="col-12" variants={fadeUp} custom={4}>
                  <label className="text-dark form-label" htmlFor="messages">
                    Message
                  </label>
                  <textarea
                    className="form-control"
                    id="messages"
                    rows="5"
                    placeholder="Messages"
                  ></textarea>
                </motion.div>

                <motion.div className="col-12" variants={fadeUp} custom={5}>
                  <motion.button
                    type="submit"
                    className="btn btn-primary px-7 rounded-pill mt-2"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Submit
                  </motion.button>
                </motion.div>

                <motion.i className="text-danger" variants={fadeUp} custom={6}>
                  Our team will respond within 24–48 working hours.
                </motion.i>
              </motion.form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Contact;
